import type { NotificationItem, NotificationVariant } from "@/lib/mock/notifications"
import {
  removeNotification,
  upsertNotification,
} from "@/lib/notifications-store"

export type ImportNotifyStatus = "running" | "done" | "imported" | "failed"

export interface ImportNotifyRecord {
  id: string
  name: string
  status: ImportNotifyStatus
  found: number
  failure?: string
  approach?: "direct" | "review"
  commit?: { imported: number }
}

export interface ImportNotificationCopy {
  title: string
  description: string
  variant: NotificationVariant
  href?: string
}

const QUESTION_BANK = "/exam-management/question-bank"

function reviewHref(id: string): string {
  return `${QUESTION_BANK}/import/${encodeURIComponent(id)}`
}

function questionLabel(count: number): string {
  return count === 1 ? "1 question" : `${count} questions`
}

function draftLabel(count: number): string {
  return count === 1 ? "1 draft" : `${count} drafts`
}

export function importNotificationId(jobId: string): string {
  return `import:${jobId}`
}

/** Copy for the notification bell and import banners. No toast — exxat-no-toast. */
export function importNotificationCopy(
  record: ImportNotifyRecord
): ImportNotificationCopy | null {
  if (record.status === "running") {
    return {
      title: `Reading ${record.name}`,
      description: "This usually takes 2 to 5 minutes. You can keep working.",
      variant: "info",
      href: QUESTION_BANK,
    }
  }

  if (record.status === "failed") {
    return {
      title: `Could not read ${record.name}`,
      description:
        record.failure ??
        "The read stopped partway. Nothing was added to the pool.",
      variant: "warning",
      href: QUESTION_BANK,
    }
  }

  if (record.status === "done") {
    if (record.found === 0) {
      return {
        title: `No questions found in ${record.name}`,
        description: "We look for numbered items with answer options.",
        variant: "info",
        href: QUESTION_BANK,
      }
    }

    if (record.approach === "direct") {
      return {
        title: `${draftLabel(record.found)} ready from ${record.name}`,
        description: "They are in Imported questions as drafts.",
        variant: "success",
        href: QUESTION_BANK,
      }
    }

    return {
      title: `${questionLabel(record.found)} found in ${record.name}`,
      description: "Nothing is in the pool yet. Review them first.",
      variant: "success",
      href: reviewHref(record.id),
    }
  }

  if (record.status === "imported") {
    const count = record.commit?.imported ?? record.found
    return {
      title: `${draftLabel(count)} imported from ${record.name}`,
      description: "Open Imported questions to add any to the section.",
      variant: "success",
      href: QUESTION_BANK,
    }
  }

  return null
}

export function notifyQuestionImportChange(record: ImportNotifyRecord) {
  const copy = importNotificationCopy(record)
  if (!copy) return

  const id = importNotificationId(record.id)
  const item: NotificationItem = {
    id,
    kind: "import",
    title: copy.title,
    description: copy.description,
    timestamp: "Just now",
    read: false,
    seen: false,
    variant: copy.variant,
    href: copy.href,
  }

  upsertNotification(item)
}

export function clearQuestionImportNotification(jobId: string) {
  removeNotification(importNotificationId(jobId))
}
